import { useRef } from 'react';
import { Button, Chip, ConfirmModal, DateInput } from '@safetyhub/ui';
import Modal from '../Modal';
import { MODAL_LAYER, SUB_MODAL_OVERLAY_BG } from '../modalLayers';
import AddressSearch from '../AddressSearch';
import { ChevronRightIcon } from '../icons';
import { VerifiedBadgeBanner } from './VerifiedBadgeBanner';
import { IndustryPicker } from './IndustryPicker';
import type { UseSiteFormStateResult } from '../../hooks/useSiteFormState';
import {
  SITE_EDITABLE_STATUS_OPTIONS,
  type SiteEditableStatus,
} from '../../lib/organizations';

const HAZARD_CATEGORIES = [
  '화학물질',
  '고소작업',
  '중량물',
  '전기작업',
  '용접작업',
  '밀폐공간',
  '분진',
  '소음',
  '진동',
  '고열작업',
];

/**
 * 국세청 조회가 **일시 장애**로 실패했을 때의 안내.
 *
 * 미등록·휴폐업·형식 오류와 달리 사용자가 입력을 고쳐서 뚫을 수 있는 실패가 아니다.
 * 그래서 빨간 에러로 막아 세우는 대신, 현장 등록 자체는 진행할 수 있는 우회로
 * ([사업자등록번호 없음])를 안내한다. 진행 버튼은 문구 그대로 체크박스를 켠다
 * (검증 상태·입력한 번호는 함께 초기화) — 명시적 클릭이 곧 사용자의 선택이므로
 * 대행이 아니다. 켜진 칸으로 스크롤해 무엇이 바뀌었는지 보여준다.
 */
function NtsOutageNotice({
  compact,
  onGoToNoRegistration,
}: {
  compact: boolean;
  onGoToNoRegistration: () => void;
}) {
  return (
    <div
      className={
        compact
          ? 'mt-1 p-2 bg-amber-50 border border-amber-200 rounded-card-sm'
          : 'mt-2 p-3 bg-amber-50 border border-amber-200 rounded-card'
      }
    >
      <p className="text-xs text-amber-900 leading-relaxed">
        국세청 사업자 정보 확인 서비스가 일시적으로 응답하지 않습니다. 지금은
        [사업자등록번호 없음]으로 현장을 먼저 등록하고, 서비스 복구 후 현장의 사업장
        개요에서 사업자번호를 등록해 주세요.
      </p>
      <button
        type="button"
        onClick={onGoToNoRegistration}
        className="mt-2 text-xs font-medium text-amber-800 underline hover:text-amber-900"
      >
        [사업자등록번호 없음]으로 진행
      </button>
    </div>
  );
}

/** 「조직과 동일」 체크박스 옆 안내 문구(사용자 확정 2026-10-29). */
export const SAME_AS_ORGANIZATION_GUIDE =
  '우리 회사가 직접 운영하는 현장일 때만 체크하세요. 고객사 현장을 대행하는 경우에는 고객사 사업자등록번호를 입력하세요.';

/** 기관(지도기관·안전관리 위탁기관)은 대개 남의 사업장을 맡으므로 안내를 경고 톤으로 띄운다. */
const AGENCY_CATEGORIES = new Set(['GUIDANCE_AGENCY', 'SAFETY_MANAGEMENT_AGENCY']);

export interface SiteFormBodyProps {
  state: UseSiteFormStateResult;
  isEdit: boolean;
  /**
   * Compact 레이아웃 (모달 내부). true이면 위도/경도 필드 숨김, 여백 축소.
   */
  compact?: boolean;
}

export function SiteFormBody({ state, isEdit, compact = false }: SiteFormBodyProps) {
  const {
    registrationNumber,
    companyName,
    setCompanyName,
    ceoName,
    setCeoName,
    openingDate,
    setOpeningDate,
    noRegistrationNumber,
    sameAsOrganization,
    handleToggleSameAsOrganization,
    organizationCategory,

    isVerifyingStatus,
    statusVerified,
    registrationNumberError,
    lookupMatchKind,

    showInfoForm,
    setShowInfoForm,
    isVerifyingInfo,
    infoVerified,
    setInfoVerified,
    infoVerifyError,
    setInfoVerifyError,
    ntsTemporaryError,

    name,
    setName,
    address,
    addressDetail,
    zonecode,
    setAddress,
    latitude,
    setLatitude,
    longitude,
    setLongitude,
    timezone,
    setTimezone,
    industryId,
    setIndustryId,
    legacyIndustryLabel,
    hazardCategories,
    toggleHazardCategory,
    status,
    setStatus,

    groupId,
    setGroupId,
    groupOptions,
    canSelectGroup,
    agencyOperatorGroupName,
    siteInfoLockedReason,
    showGroupChangeConfirm,
    handleGroupChangeConfirm,
    handleGroupChangeCancel,

    constructionCost,
    setConstructionCost,
    constructionStart,
    setConstructionStart,
    constructionEnd,
    setConstructionEnd,
    managementNumber,
    setManagementNumber,
    licenseNumber,
    setLicenseNumber,
    progressRate,
    setProgressRate,
    clientName,
    setClientName,
    clientContact,
    setClientContact,
    isConstructionOpen,
    setIsConstructionOpen,
    isConstructionIndustry,

    isLoading,

    companyReady,
    badgeName,

    handleVerifyRegistrationNumber,
    handleVerifyInfo,
    handleFillSiteNameFromCompany,
    handleToggleNoRegistration,
    handleRegistrationNumberChange,

    showMismatchModal,
    setShowMismatchModal,
    handleMismatchRetryVerification,
    handleMismatchSaveWithoutVerification,
  } = state;

  const sectionCardClass = compact
    ? 'min-w-0'
    : 'bg-white shadow rounded-card-lg p-6';
  const sectionTitleClass = compact
    ? 'text-sm font-medium text-ink-900 mb-3'
    : 'text-lg font-medium text-ink-900 mb-4';
  const inputClass = compact
    ? 'block w-full min-w-0 px-2 py-1.5 border border-ink-300 rounded-control text-sm focus:ring-brand-500 focus:border-brand-500'
    : 'mt-1 block w-full px-3 py-2 border border-ink-300 rounded-control shadow-sm focus:ring-brand-500 focus:border-brand-500';
  const disabledInputClass = compact
    ? `${inputClass} disabled:bg-ink-100`
    : inputClass;
  // 공용 `DateInput` 에 넘기는 것은 **폭과 바깥 여백만**이다 — 패딩·테두리·포커스는 그
  // 컴포넌트의 `size` 가 소유하므로 위 `inputClass` 를 그대로 넘기면 두 벌이 겹친다.
  // 비compact 쪽에 `mt-1` 이 필요한 이유는 그 값이 `inputClass` 안에 섞여 있었기 때문이고
  // (라벨과의 간격), compact 는 라벨이 `mb-1` 로 간격을 갖는다.
  const dateTriggerClass = compact ? 'w-full' : 'mt-1 w-full';
  const subLabelClass = 'block text-xs font-medium text-ink-700 mb-1';
  const labelClass = compact
    ? 'block text-xs font-medium text-ink-700 mb-1'
    : 'block text-sm font-medium text-ink-700';

  // compact 폼은 입력 높이가 낮아 sm(32px), 일반 폼은 정본 md(38px).
  const verifyBtnSize = compact ? 'sm' : 'md';

  const iconBadgeCompact = compact ? 'h-4 w-4' : 'h-5 w-5';

  // 사업자 입력(번호 검증·4필드) 영역이 보이는가 — 두 우회(번호 없음 · 조직과 동일) 모두 아닐 때만.
  const manualEntry = !noRegistrationNumber && !sameAsOrganization;
  const isAgencyOrganization =
    organizationCategory !== null && AGENCY_CATEGORIES.has(organizationCategory);

  const noRegistrationRef = useRef<HTMLInputElement>(null);
  const goToNoRegistration = () => {
    handleToggleNoRegistration(true);
    const el = noRegistrationRef.current;
    if (!el) return;
    el.scrollIntoView({ block: 'center', behavior: 'smooth' });
    el.focus();
  };

  return (
    <div className={compact ? 'space-y-6' : 'space-y-6'}>
      {/* O10 — 정보 관리권이 업체에 있는 현장(저장 버튼은 `canSubmit`이 막는다). `/sites/:id/edit`를 직접 연 경우의 안내 */}
      {siteInfoLockedReason !== null && (
        <div
          role="alert"
          data-testid="site-info-locked-notice"
          className="p-3 text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded-card"
        >
          {siteInfoLockedReason}
        </div>
      )}
      {/* 사업자 정보 검증 */}
      <div className={sectionCardClass}>
        <h3 className={sectionTitleClass}>사업자 정보 검증</h3>

        <div
          className={
            compact
              ? 'p-3 border border-line rounded-card space-y-3 min-w-0'
              : 'space-y-4'
          }
        >
          <div className="min-w-0">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={sameAsOrganization}
                onChange={(e) => handleToggleSameAsOrganization(e.target.checked)}
                disabled={isLoading}
                className="h-4 w-4 text-brand-600"
              />
              <span className="text-sm text-ink-700">조직과 동일</span>
            </label>
            {isAgencyOrganization ? (
              <p
                data-testid="same-as-organization-guide"
                data-tone="warning"
                className="mt-1 p-2 text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded-card-sm leading-relaxed"
              >
                {SAME_AS_ORGANIZATION_GUIDE}
              </p>
            ) : (
              <p
                data-testid="same-as-organization-guide"
                data-tone="hint"
                className="mt-1 text-xs text-ink-500"
              >
                {SAME_AS_ORGANIZATION_GUIDE}
              </p>
            )}
          </div>

          {/* 조직과 동일 — 무엇과 연결되는지 보이게 조직 회사 정보를 읽기 전용으로 */}
          {sameAsOrganization && (
            <div
              className={
                compact
                  ? 'grid grid-cols-1 md:grid-cols-2 gap-2 min-w-0'
                  : 'grid grid-cols-1 md:grid-cols-2 gap-3'
              }
            >
              <div className="min-w-0">
                <label className={subLabelClass}>사업자등록번호</label>
                <input
                  type="text"
                  value={registrationNumber}
                  readOnly
                  disabled
                  className="block w-full min-w-0 px-2 py-1.5 border border-ink-300 rounded-control text-sm bg-ink-100"
                />
              </div>
              <div className="min-w-0">
                <label className={subLabelClass}>상호</label>
                <input
                  type="text"
                  value={companyName}
                  readOnly
                  disabled
                  className="block w-full min-w-0 px-2 py-1.5 border border-ink-300 rounded-control text-sm bg-ink-100"
                />
              </div>
            </div>
          )}

          <label className="flex items-center gap-2 cursor-pointer">
            <input
              ref={noRegistrationRef}
              type="checkbox"
              checked={noRegistrationNumber}
              onChange={(e) => handleToggleNoRegistration(e.target.checked)}
              disabled={isLoading}
              className="h-4 w-4 text-brand-600"
            />
            <span className="text-sm text-ink-700">
              사업자등록번호 없음 (개인/공공/미등록)
            </span>
          </label>

          {manualEntry && (
            <div className="min-w-0">
              <label className={compact ? subLabelClass : `${labelClass} mb-1`}>
                사업자등록번호 <span className="text-red-500">*</span>
              </label>
              <div className="flex gap-2 min-w-0">
                <input
                  type="text"
                  value={registrationNumber}
                  onChange={(e) => handleRegistrationNumberChange(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      if (
                        !isVerifyingStatus &&
                        registrationNumber.trim().length > 0
                      ) {
                        void handleVerifyRegistrationNumber();
                      }
                    }
                  }}
                  disabled={statusVerified || isVerifyingStatus || isLoading}
                  placeholder="123-45-67890"
                  className={
                    compact
                      ? `${disabledInputClass} disabled:bg-ink-100`
                      : 'block w-full min-w-0 px-3 py-2 border border-ink-300 rounded-control text-sm focus:ring-brand-500 focus:border-brand-500 disabled:bg-ink-100'
                  }
                />
                <Button
                  variant="primary"
                  size={verifyBtnSize}
                  onClick={() => void handleVerifyRegistrationNumber()}
                  disabled={isVerifyingStatus || statusVerified || isLoading}
                  className="whitespace-nowrap"
                >
                  {isVerifyingStatus
                    ? '검증 중...'
                    : statusVerified
                      ? '검증 완료'
                      : '번호 검증'}
                </Button>
              </div>
              {registrationNumberError && ntsTemporaryError ? (
                <NtsOutageNotice
                  compact={compact}
                  onGoToNoRegistration={goToNoRegistration}
                />
              ) : registrationNumberError ? (
                <p className="mt-1 text-xs text-red-600">
                  {registrationNumberError}
                </p>
              ) : !statusVerified ? (
                <p className="mt-1 text-xs text-ink-500">
                  사업자등록번호 10자리를 입력하고 [번호 검증]을 눌러주세요.
                </p>
              ) : null}
            </div>
          )}

          {/* FULLY 매칭: "검증 완료된 사업자" 배지 */}
          {manualEntry &&
            statusVerified &&
            infoVerified &&
            lookupMatchKind === 'fully' && (
              <VerifiedBadgeBanner
                kind="fully"
                companyName={companyName}
                compact={compact}
              />
            )}

          {/* 조직 전용 매칭: "우리 조직에 등록된 사업자 정보" 배지 (수정 가능) */}
          {manualEntry &&
            statusVerified &&
            infoVerified &&
            lookupMatchKind === 'org' && (
              <VerifiedBadgeBanner
                kind="org"
                companyName={companyName}
                compact={compact}
                onEdit={() => {
                  setInfoVerified(false);
                  setShowInfoForm(true);
                  setInfoVerifyError(null);
                }}
              />
            )}

          {/* 4필드 검증 완료 배지 (FULLY로 승격된 직후 — lookupMatchKind 미확정) */}
          {manualEntry &&
            statusVerified &&
            infoVerified &&
            lookupMatchKind === null && (
              <VerifiedBadgeBanner
                kind="verified"
                companyName={companyName}
                compact={compact}
                onEdit={() => {
                  setInfoVerified(false);
                  setShowInfoForm(true);
                  setInfoVerifyError(null);
                }}
              />
            )}

          {/* 번호 검증 완료 (운영 중) + 4필드 검증 미완료 */}
          {manualEntry && statusVerified && !infoVerified && (
            <div className="flex items-center gap-2 p-3 bg-yellow-50 border border-yellow-200 rounded-card">
              <svg
                className={`${iconBadgeCompact} text-yellow-600 flex-shrink-0`}
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V5z"
                  clipRule="evenodd"
                />
              </svg>
              <span className="text-sm text-yellow-900 min-w-0 truncate">
                <span className="font-medium">운영 중</span> (번호 확인)
              </span>
              {!showInfoForm && (
                <button
                  type="button"
                  onClick={() => setShowInfoForm(true)}
                  className="ml-auto text-sm font-medium text-brand-700 underline hover:text-brand-900 flex-shrink-0"
                  title="사업자 정보를 국세청에서 확인합니다"
                >
                  사업자 정보 검증
                </button>
              )}
            </div>
          )}

          {/* 4필드 입력 영역 */}
          {manualEntry && statusVerified && showInfoForm && (
            <div
              className={
                compact
                  ? 'p-2 bg-surface-soft border border-line rounded-card-sm space-y-2'
                  : 'p-3 bg-surface-soft border border-line rounded-card space-y-3'
              }
            >
              <p className="text-xs text-ink-600">
                상호·대표자·개업일을 입력하고 [확인]을 눌러 사업자 정보를
                국세청에서 검증합니다.
              </p>
              <div
                className={
                  compact
                    ? 'grid grid-cols-1 md:grid-cols-3 gap-2 min-w-0'
                    : 'grid grid-cols-1 md:grid-cols-3 gap-3'
                }
              >
                <div className="min-w-0">
                  <label className={subLabelClass}>
                    상호 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={companyName}
                    onChange={(e) => {
                      setCompanyName(e.target.value);
                      setInfoVerifyError(null);
                    }}
                    disabled={isLoading || isVerifyingInfo}
                    placeholder="예: (주)안전건설"
                    className="block w-full min-w-0 px-2 py-1.5 border border-ink-300 rounded-control text-sm focus:ring-brand-500 focus:border-brand-500"
                  />
                </div>
                <div className="min-w-0">
                  <label className={subLabelClass}>
                    대표자명 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={ceoName}
                    onChange={(e) => {
                      setCeoName(e.target.value);
                      setInfoVerifyError(null);
                    }}
                    disabled={isLoading || isVerifyingInfo}
                    placeholder="예: 홍길동"
                    className="block w-full min-w-0 px-2 py-1.5 border border-ink-300 rounded-control text-sm focus:ring-brand-500 focus:border-brand-500"
                  />
                </div>
                <div className="min-w-0">
                  <label className={subLabelClass}>
                    개업일 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={openingDate}
                    onChange={(e) => {
                      setOpeningDate(
                        e.target.value.replace(/\D/g, '').slice(0, 8),
                      );
                      setInfoVerifyError(null);
                    }}
                    disabled={isLoading || isVerifyingInfo}
                    placeholder="YYYYMMDD"
                    className="block w-full min-w-0 px-2 py-1.5 border border-ink-300 rounded-control text-sm focus:ring-brand-500 focus:border-brand-500"
                  />
                </div>
              </div>
              {infoVerifyError &&
                (ntsTemporaryError ? (
                  <NtsOutageNotice
                    compact={compact}
                    onGoToNoRegistration={goToNoRegistration}
                  />
                ) : (
                  <p className="text-xs text-red-600">{infoVerifyError}</p>
                ))}
              <div className="flex justify-end gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    setShowInfoForm(false);
                    setInfoVerifyError(null);
                  }}
                  disabled={isLoading || isVerifyingInfo}
                >
                  취소
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => void handleVerifyInfo()}
                  disabled={isLoading || isVerifyingInfo}
                  title="사업자 정보를 국세청에서 확인합니다"
                >
                  {isVerifyingInfo ? '확인 중...' : '확인'}
                </Button>
              </div>
            </div>
          )}

          {/* 사업자번호 없음 경로 */}
          {noRegistrationNumber && (
            <div className={compact ? 'space-y-2' : 'space-y-3'}>
              <div
                className={
                  compact
                    ? 'flex items-center gap-2 p-2 bg-ink-100 border border-ink-200 rounded-card-sm'
                    : 'flex items-center gap-2 p-3 bg-ink-100 border border-ink-200 rounded-card'
                }
              >
                <svg
                  className={`${iconBadgeCompact} text-ink-600 flex-shrink-0`}
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                    clipRule="evenodd"
                  />
                </svg>
                <span className="text-sm text-ink-700">
                  검증 대상 아님 (개인/공공/미등록)
                </span>
              </div>
              <div
                className={
                  compact
                    ? 'grid grid-cols-1 md:grid-cols-3 gap-2 min-w-0'
                    : 'grid grid-cols-1 md:grid-cols-3 gap-3'
                }
              >
                <div className="min-w-0">
                  <label className={subLabelClass}>상호</label>
                  <input
                    type="text"
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    disabled={isLoading}
                    placeholder="예: 홍길동 컨설팅"
                    className="block w-full min-w-0 px-2 py-1.5 border border-ink-300 rounded-control text-sm focus:ring-brand-500 focus:border-brand-500"
                  />
                  <p className="mt-1 text-xs text-ink-500">
                    모르시면 비워두세요. 현장명으로 자동 저장됩니다.
                  </p>
                </div>
                <div className="min-w-0">
                  <label className={subLabelClass}>대표자명</label>
                  <input
                    type="text"
                    value={ceoName}
                    onChange={(e) => setCeoName(e.target.value)}
                    disabled={isLoading}
                    placeholder="예: 홍길동"
                    className="block w-full min-w-0 px-2 py-1.5 border border-ink-300 rounded-control text-sm focus:ring-brand-500 focus:border-brand-500"
                  />
                </div>
                <div className="min-w-0">
                  <label className={subLabelClass}>개업일</label>
                  <input
                    type="text"
                    value={openingDate}
                    onChange={(e) =>
                      setOpeningDate(
                        e.target.value.replace(/\D/g, '').slice(0, 8),
                      )
                    }
                    disabled={isLoading}
                    placeholder="YYYYMMDD"
                    className="block w-full min-w-0 px-2 py-1.5 border border-ink-300 rounded-control text-sm focus:ring-brand-500 focus:border-brand-500"
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* 현장 정보 + 업종 + 위험 + 건설 — fieldset disable */}
      <fieldset
        disabled={!companyReady}
        className={!companyReady ? 'opacity-50 pointer-events-none' : ''}
      >
        <div className={sectionCardClass}>
          <h3 className={sectionTitleClass}>현장 정보</h3>
          {!companyReady && (
            <p
              className={
                compact ? 'mb-2 text-xs text-ink-500' : 'mb-3 text-xs text-ink-500'
              }
            >
              사업자 정보 검증 후 입력할 수 있습니다.
            </p>
          )}
          <div className="space-y-4">
            <div>
              <div className="flex items-center justify-between">
                <label htmlFor="site-name" className={labelClass}>
                  현장 이름 <span className="text-red-500">*</span>
                </label>
                {badgeName && !name.trim() && (
                  <button
                    type="button"
                    onClick={handleFillSiteNameFromCompany}
                    className="text-xs text-brand-600 hover:text-brand-800 underline"
                  >
                    상호로 채우기
                  </button>
                )}
              </div>
              <input
                type="text"
                id="site-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={isLoading}
                className={disabledInputClass}
                placeholder="예: A공장, 본사 사옥"
                autoFocus={compact}
              />
            </div>

            <div>
              <label className={`${labelClass} mb-1`}>
                현장 주소 <span className="text-red-500">*</span>
              </label>
              <AddressSearch
                id="site-address"
                address={address}
                addressDetail={addressDetail}
                zonecode={zonecode}
                onChange={setAddress}
                disabled={isLoading}
              />
            </div>

            {!compact && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="latitude" className={labelClass}>
                    위도
                  </label>
                  <input
                    type="number"
                    id="latitude"
                    step="any"
                    value={latitude}
                    onChange={(e) => setLatitude(e.target.value)}
                    className={inputClass}
                    placeholder="37.5665"
                  />
                </div>
                <div>
                  <label htmlFor="longitude" className={labelClass}>
                    경도
                  </label>
                  <input
                    type="number"
                    id="longitude"
                    step="any"
                    value={longitude}
                    onChange={(e) => setLongitude(e.target.value)}
                    className={inputClass}
                    placeholder="126.9780"
                  />
                </div>
              </div>
            )}

            <div>
              <label htmlFor="site-timezone" className={labelClass}>
                시간대
              </label>
              <select
                id="site-timezone"
                value={timezone}
                onChange={(e) => setTimezone(e.target.value)}
                disabled={isLoading}
                className={disabledInputClass}
              >
                <option value="Asia/Seoul">Asia/Seoul (KST, +09:00)</option>
                <option value="Asia/Tokyo">Asia/Tokyo (JST, +09:00)</option>
                <option value="Asia/Shanghai">Asia/Shanghai (CST, +08:00)</option>
                <option value="UTC">UTC (협정 세계시)</option>
              </select>
              {!compact && (
                <p className="mt-1 text-xs text-ink-500">
                  설정하지 않으면 조직의 시간대를 사용합니다.
                </p>
              )}
            </div>

            {/* 소속 그룹 — Owner이면서 그룹이 2개 이상일 때만 (1개면 본점 자동 배정) */}
            {canSelectGroup && (
              <div>
                <label htmlFor="site-group" className={labelClass}>
                  소속 그룹
                </label>
                <select
                  id="site-group"
                  value={groupId}
                  onChange={(e) => setGroupId(e.target.value)}
                  disabled={isLoading}
                  className={disabledInputClass}
                >
                  {groupOptions.map((g) => (
                    <option key={g.id} value={g.id}>
                      {g.isDefault ? `${g.name} (기본)` : g.name}
                    </option>
                  ))}
                </select>
                {isEdit && (
                  <p className="mt-1 text-xs text-ink-500">
                    그룹을 바꾸면 이 현장과 소속 데이터(보고서·점검·스케줄)가 함께
                    이동합니다.
                  </p>
                )}
              </div>
            )}

            {/* 대행 현장 — 소속 그룹은 업체(소유 조직)의 것이라 바꿀 수 없다. 우리 배치 그룹만 읽기 전용으로 (P4) */}
            {agencyOperatorGroupName !== null && (
              <div data-testid="site-operator-group">
                <span className={labelClass}>배치 그룹</span>
                <p className="text-sm text-ink-900">{agencyOperatorGroupName}</p>
                <p className="mt-1 text-xs text-ink-500">
                  대행 현장이라 이 화면에서는 그룹을 바꿀 수 없습니다.
                </p>
              </div>
            )}
          </div>
        </div>

        <div className={compact ? sectionCardClass : `${sectionCardClass} mt-6`}>
          <h3 className={sectionTitleClass}>업종 정보</h3>
          <div className="space-y-4">
            {/* 업종 분류(KSIC) — 현장 업종의 유일한 입력이다(2026-10-30 — 8개 목록·「세부 업종」 자유 텍스트를 대체).
                법정 서류 적용 판정·AI 업종 템플릿·건설 단계 노출이 이 칸에서 파생한다 */}
            <div>
              <span className={labelClass}>업종 분류(한국표준산업분류)</span>
              <IndustryPicker
                value={industryId}
                onChange={setIndustryId}
                disabled={isLoading}
                selectClassName={disabledInputClass}
                labelClassName={labelClass}
              />
              {/* 옛 자유 텍스트 업종 — 읽기 전용 단서(KSIC를 아직 안 고른 현장에서만) */}
              {!industryId && legacyIndustryLabel && (
                <p className="mt-1 text-xs text-ink-500">
                  기존에 입력한 업종: {legacyIndustryLabel}
                </p>
              )}
            </div>
          </div>
        </div>

        <div className={compact ? sectionCardClass : `${sectionCardClass} mt-6`}>
          <h3 className={sectionTitleClass}>위험 카테고리</h3>
          <p
            className={
              compact
                ? 'text-xs text-ink-500 mb-3'
                : 'text-sm text-ink-500 mb-3'
            }
          >
            현장에 해당하는 위험 요소를 선택하세요.{' '}
            {compact ? '위험요인 분석 시 참고됩니다.' : 'AI 분석 시 참고됩니다.'}
          </p>
          <div className="flex flex-wrap gap-2">
            {/* 다중 선택 토글 — 선택 색이 yellow에서 brand로 바뀐다(Chip은 선택 색이 하나다). */}
            {HAZARD_CATEGORIES.map((category) => (
              <Chip
                key={category}
                selected={hazardCategories.includes(category)}
                onClick={() => toggleHazardCategory(category)}
                disabled={isLoading}
              >
                {category}
              </Chip>
            ))}
          </div>
        </div>

        {isEdit && (
        <div className={compact ? sectionCardClass : `${sectionCardClass} mt-6`}>
          <button
            type="button"
            onClick={() => setIsConstructionOpen((prev) => !prev)}
            className="flex items-center w-full text-left"
          >
            <ChevronRightIcon
              className={`${compact ? 'h-4 w-4' : 'h-5 w-5'} mr-1 text-ink-500 transition-transform ${
                isConstructionOpen || isConstructionIndustry ? 'rotate-90' : ''
              }`}
            />
            <h3 className={sectionTitleClass.replace(' mb-3', '').replace(' mb-4', '')}>
              건설 현장 정보
            </h3>
            {!isConstructionIndustry && (
              <span
                className={
                  compact
                    ? 'ml-2 text-xs text-ink-400'
                    : 'ml-2 text-sm text-ink-400'
                }
              >
                (건설업 해당 시 입력)
              </span>
            )}
          </button>
          {(isConstructionOpen || isConstructionIndustry) && (
            <div
              className={
                compact ? 'mt-3 space-y-4 pl-5' : 'mt-4 space-y-4 pl-6'
              }
            >
              <div>
                <label htmlFor="construction-cost" className={labelClass}>
                  공사금액 (원)
                </label>
                <input
                  type="number"
                  id="construction-cost"
                  value={constructionCost}
                  onChange={(e) => setConstructionCost(e.target.value)}
                  disabled={isLoading}
                  min="0"
                  className={disabledInputClass}
                  placeholder="예: 500000000"
                />
              </div>
              <div className={compact ? 'grid grid-cols-2 gap-3' : 'grid grid-cols-2 gap-4'}>
                <div>
                  <label htmlFor="construction-start" className={labelClass}>
                    착공일
                  </label>
                  <DateInput
                    label="착공일"
                    id="construction-start"
                    size={compact ? 'sm' : 'md'}
                    className={dateTriggerClass}
                    value={constructionStart}
                    onChange={setConstructionStart}
                    disabled={isLoading}
                  />
                </div>
                <div>
                  <label htmlFor="construction-end" className={labelClass}>
                    준공일
                  </label>
                  <DateInput
                    label="준공일"
                    id="construction-end"
                    size={compact ? 'sm' : 'md'}
                    className={dateTriggerClass}
                    value={constructionEnd}
                    onChange={setConstructionEnd}
                    disabled={isLoading}
                  />
                </div>
              </div>
              <div>
                <label htmlFor="management-number" className={labelClass}>
                  사업장관리번호
                </label>
                <input
                  type="text"
                  id="management-number"
                  value={managementNumber}
                  onChange={(e) => setManagementNumber(e.target.value)}
                  disabled={isLoading}
                  className={disabledInputClass}
                  placeholder="예: 123-45-67890"
                />
              </div>
              <div>
                <label htmlFor="site-license-number" className={labelClass}>
                  면허번호
                </label>
                <input
                  type="text"
                  id="site-license-number"
                  value={licenseNumber}
                  onChange={(e) => setLicenseNumber(e.target.value)}
                  disabled={isLoading}
                  className={disabledInputClass}
                  placeholder="예: 종합 제1234호"
                />
              </div>
              <div>
                <label htmlFor="progress-rate" className={labelClass}>
                  공정률 (%)
                </label>
                <input
                  type="number"
                  id="progress-rate"
                  value={progressRate}
                  onChange={(e) => setProgressRate(e.target.value)}
                  disabled={isLoading}
                  min="0"
                  max="100"
                  className={disabledInputClass}
                  placeholder="예: 45"
                />
              </div>
              <div>
                <label htmlFor="client-name" className={labelClass}>
                  발주자명
                </label>
                <input
                  type="text"
                  id="client-name"
                  value={clientName}
                  onChange={(e) => setClientName(e.target.value)}
                  disabled={isLoading}
                  className={disabledInputClass}
                  placeholder="예: 한국도로공사"
                />
              </div>
              <div>
                <label htmlFor="client-contact" className={labelClass}>
                  발주자 연락처
                </label>
                <input
                  type="text"
                  id="client-contact"
                  value={clientContact}
                  onChange={(e) => setClientContact(e.target.value)}
                  disabled={isLoading}
                  className={disabledInputClass}
                  placeholder="예: 02-1234-5678"
                />
              </div>
            </div>
          )}
        </div>
        )}

        {isEdit && (
          <div className={compact ? sectionCardClass : `${sectionCardClass} mt-6`}>
            <h3 className={sectionTitleClass}>현장 상태</h3>
            {/* 체크박스(활성/비활성)로는 "잠시 멈춤"과 "끝남"을 구분할 수 없어 생애주기
                선택으로 바꿨다. '이관중'은 이관 플로우가 소유하는 상태라 선택지에 없다. */}
            <label htmlFor="site-status" className={labelClass}>
              운영 상태
            </label>
            <select
              id="site-status"
              value={status}
              onChange={(e) => setStatus(e.target.value as SiteEditableStatus)}
              disabled={isLoading}
              className={disabledInputClass}
            >
              {SITE_EDITABLE_STATUS_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-ink-500">
              일시중지한 현장은 점검·보고서 생성 선택지에서 빠집니다(재개하면 돌아옵니다).
              종료한 현장은 새 점검을 만들 수 없고, 기존 점검의 서류 마무리만 가능합니다.
            </p>
          </div>
        )}
      </fieldset>

      {/* 에러 모달: COMPANY_INFO_MISMATCH */}
      <Modal
        isOpen={showMismatchModal}
        onClose={() => setShowMismatchModal(false)}
        title="사업자 정보가 일치하지 않습니다"
        size="sm"
        footer={
          <>
            <Button variant="secondary" onClick={handleMismatchRetryVerification} disabled={isLoading}>
              다시 확인
            </Button>
            <Button
              variant="primary"
              onClick={() => void handleMismatchSaveWithoutVerification()}
              disabled={isLoading}
            >
              검증 없이 저장
            </Button>
          </>
        }
      >
        <div className="space-y-3 p-1">
          <p className="text-sm text-ink-700">
            국세청 등록 정보와 다른 값이 있습니다. 정보를 다시 확인하거나, 검증
            없이 저장할 수 있습니다 (추후 재검증 가능).
          </p>
        </div>
      </Modal>

      {/* 그룹 이동 확인 — 접근 범위가 바뀌는 조작이라 danger(취소가 default).
          compact(모달 내부)에서는 폼 모달(root) 위에 떠야 하므로 sub 층을 주입한다. */}
      <ConfirmModal
        open={showGroupChangeConfirm}
        title="현장 그룹 이동"
        body="이 현장과 소속 데이터(보고서·점검·스케줄)가 선택한 그룹으로 이동합니다. 새 그룹에 속하지 않은 배정 멤버는 이 현장에 접근할 수 없게 됩니다."
        confirmLabel="이동하고 저장"
        danger
        busy={isLoading}
        overlayZClassName={compact ? MODAL_LAYER.sub : undefined}
        overlayBgClassName={compact ? SUB_MODAL_OVERLAY_BG : undefined}
        onConfirm={() => void handleGroupChangeConfirm()}
        onClose={handleGroupChangeCancel}
      />
    </div>
  );
}
